<template>
  <article>
    <div id="AUTH" class="container-fixed offset-bottom-6">
      <VProgress class="offset-top-2 offset-bottom-2" :steps="steps" active="auth.identification" />
      <p class="invisible-empty" v-html="$t('auth.pageDescription')"></p>
      <div v-if="$t('auth.manualAuthHintText') !== ''">
        <div v-if="isItsEnabled" class="notification notification-warning">
          <div class="notification-content" v-html="$tc('auth.manualAuthHintText', 'auth/its/retry?sessionId=' + sessionId)"> </div>
        </div>
        <hr />
      </div>
      <p class="invisible-empty offset-bottom-2 offset-top-2" v-html="$t('auth.descriptionStep1' + (captcha ? 'captcha' : ''))"></p>
      <transition name="fade" mode="out-in" appear>
        <VForm v-if="form" :form="form" @submit="onNext">
          <VInput
            name="msisdn"
            :label="$t('auth.msisdnLabel')"
            :hint="$t('errorcodes.MSISDN_FORMAT_ERROR')"
            :placeholder="$t('auth.msisdnPlaceholder')"
            required
          />

          <template v-if="captcha">
            <div class="form-input-set">
              <label for="image_captcha" v-html="$t('auth.captchaImageLabel')"></label>
              <div class="align">
                <img id="image_captcha" alt="captcha" :src="'data:image/gif;base64,' + captcha" class="offset-right-1" />
                <VButton
                  id="captcha_reload"
                  type="button"
                  label="auth.captchaReloadLabel"
                  class="btn-default"
                  :disabled="!isCaptchaReloadAllowed"
                  @click="onCaptchaReload"
                />
              </div>
            </div>

            <VInput name="captcha" :label="$t('auth.captchaLabel')" :hint="$t('errorcodes.CAPTCHA_INVALID')" required />
          </template>

          <div class="offset-top-2 text-right">
            <transition name="fade" mode="out-in" appear>
              <VSpinner v-if="isBusy" slim class="pull-left" />
            </transition>
            <VButton type="submit" class="offset-left-1 btn btn-default btn-brand" label="auth.buttonLabelStep1" :disabled="isBusy" />
          </div>
        </VForm>
        <VSpinner v-else />
      </transition>
    </div>
  </article>
</template>

<script>
import { mapState } from 'vuex';
import VSpinner from '@/components/Spinner';
import TitleMixin from '@/mixins/title';
import AuthStepsMixin from '@/mixins/authSteps';
import VForm from '@/components/Form/Form';
import VInput from '@/components/Form/Input';
import { focusFirstError, formattedPhone, hasMaxLength, hasMinLength, initFields, match } from '@/utils/form';
import Proxy from '@/proxies/AuthProxy';
import { AUTH_CONFIRMATION } from '@route-types';

export default {
  name: 'AuthIdentification',

  components: {
    VSpinner,
    VForm,
    VInput,
    VProgress: () => import('@/components/Progress'),
    VButton: () => import('@/components/Button'),
  },

  mixins: [TitleMixin, AuthStepsMixin],

  data() {
    return {
      captcha: null,
      isCaptchaReloadAllowed: null,
      isItsEnabled: null,
      form: null,
      isBusy: false,
    };
  },

  computed: {
    ...mapState('system', ['sessionId']),
  },

  async created() {
    if (Object.keys(this.$route.query).length) {
      this.$router.replace({
        ...this.$route,
        query: {},
      });
    }
    const response = await new Proxy().init();

    this.captcha = response.captcha;
    this.isCaptchaReloadAllowed = response.isCaptchaReloadAllowed;
    this.isItsEnabled = response.isItsEnabled;

    const fields = [
      {
        name: 'msisdn',
        validators: [
          hasMinLength(response.msisdnMinLength),
          hasMaxLength(response.msisdnMaxLength),
          { handler: match(new RegExp(`^${response.msisdnCountryCode}`, 'g')), hint: 'errorcodes.MSISDN_AREA_CODE_ERROR' },
        ],
        filters: [formattedPhone(response.msisdnCountryCode)],
      },
    ];

    if (this.captcha) {
      fields.push({ name: 'captcha' });
    }

    this.form = initFields(...fields);

    this.$webtrekk.viewPage('authsite.onetimepin.identification', 'authentication.common');
  },

  methods: {
    async onNext({ values, form }) {
      try {
        this.isBusy = true;
        await new Proxy().identify(values);
        this.$router.push({ name: AUTH_CONFIRMATION, params: { msisdn: values.msisdn } }); // we intentionally don't store the msisdn. will not survive f5
      } catch (error) {
        const code = error?.data?.code;
        if (code === 'INVALID_CAPTCHA') {
          form.captcha.setInvalid();
        } else if (code === 'MSISDN_FORMAT_ERROR') {
          form.msisdn.setInvalid();
        } else if (code === 'MSISDN_AREA_CODE_ERROR') {
          form.msisdn.setInvalid(this.$t('errorcodes.MSISDN_AREA_CODE_ERROR'));
        } else {
          throw error;
        }
        focusFirstError(form);
        this.isBusy = false;
      }
    },

    async onCaptchaReload() {
      this.isBusy = true;
      const response = await new Proxy().captchaReload();
      this.captcha = response.captcha;
      this.isCaptchaReloadAllowed = response.isCaptchaReloadAllowed;
      this.isBusy = false;
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@scss';

.notification {
  padding-bottom: 16px;
  margin-top: 12px;
}
#image_captcha {
  border: 1px solid #b2b2b2;
  border-radius: 4px;
}

@media screen and (max-width: #{$grid-small-max}px) {
  #captcha_reload {
    width: 100%;
  }
}

@media screen and (min-width: #{$grid-medium-min}px) {
  .align {
    display: flex;
    align-items: center;
  }
}
</style>
